import { useState, useCallback, useMemo } from 'react';
import { normalizeCompensationData } from '../utils/dataProcessing/normalizeData';
import { sortPositionsByLevel, filterPositionsByDepartment, extractDepartments } from '../utils/dataProcessing/positionSorting';

export function useCompensationData() {
  const [files, setFiles] = useState([]);
  const [loadedData, setLoadedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract unique departments from loaded data
  const departments = useMemo(() => {
    return extractDepartments(loadedData);
  }, [loadedData]);

  // Handle file upload and data loading
  const handleFileUpload = useCallback(async (event) => {
    const newFiles = [...event.target.files];
    setFiles(prev => [...prev, ...newFiles]);
    setIsLoading(true);
    setError(null);

    try {
      const newLoadedData = await Promise.all(
        newFiles.map(async (file) => {
          const text = await file.text();
          try {
            const rawData = JSON.parse(text);
            const normalizedData = normalizeCompensationData({
              ...rawData,
              fileName: file.name
            });
            return normalizedData;
          } catch (error) {
            console.error(`Error parsing ${file.name}:`, error);
            return null;
          }
        })
      );

      const validData = newLoadedData.filter(d => d !== null);
      if (validData.length === 0 && newFiles.length > 0) {
        setError('Keine gültigen Compensation Reports gefunden');
      }

      setLoadedData(prev => [...prev, ...validData]);
    } catch (error) {
      console.error('Error loading files:', error);
      setError('Fehler beim Laden der Dateien');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reset all data
  const resetAllFiles = useCallback(() => {
    setFiles([]);
    setLoadedData([]);
    setError(null);
  }, []);

  // Filter data by department
  const filterByDepartment = useCallback((department) => {
    return filterPositionsByDepartment(loadedData, department);
  }, [loadedData]);

  // Sort positions by level
  const getSortedPositions = useCallback((data) => {
    return sortPositionsByLevel(data);
  }, []);

  return {
    files,
    loadedData,
    isLoading,
    error,
    departments,
    handleFileUpload,
    resetAllFiles,
    filterByDepartment,
    getSortedPositions
  };
}